import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

import withRouter from "../../components/Common/withRouter";

// Redux
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../store/auth/login/actions";

// API
import { post } from "../../api/axios";
import endpoints from "../../api/endpoints";

// Utils
import { extractErrorMessage } from "../../utils/errorHandler";

// Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

// reusable components
import CustomInput from "../../components/AuthenticationComponents/CustomInput";
import CustomButton from "../../components/AuthenticationComponents/CustomButton";
import logo from "../../assets/images/authentication/logo-auth.svg";
import "../../assets/scss/custom/pages/Authentication.scss";
import zapIcon from "../../assets/images/authentication/zap.svg";
import creditCardIcon from "../../assets/images/authentication/credit-card.svg";
import giftIcon from "../../assets/images/authentication/gift.svg";
import arrowRightIcon from "../../assets/images/authentication/arrow-right.svg";

const RestaurantTerminalLogin = (props) => {
  document.title = "Terminal Login | Omaha Food Lovers";

  const dispatch = useDispatch();

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      code: "",
    },
    validationSchema: Yup.object({
      code: Yup.string()
        .trim()
        .required("Please enter your terminal code")
        .min(6, "Code must be at least 6 characters"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      try {
        const response = await post(endpoints.restaurantTerminalCodeLogin, {
          code: values.code,
        });

        const isSuccess = response.data?.success || response.data?.status;
        const userData = response.data?.data || response.data?.user;
        const token = userData?.token;

        if (isSuccess && userData && token) {
          // Store token in localStorage
          localStorage.setItem("accessToken", token);
          localStorage.setItem("user", JSON.stringify(userData));

          // Store in Redux
          dispatch(loginSuccess(userData));

          toastr.success(
            response.data?.message || response.message || "Login successful!",
          );
          props.router.navigate("/restaurant/dashboard");
        } else {
          setError(
            response.data?.message || response.message || "Login failed",
          );
          toastr.error(
            response.data?.message || response.message || "Login failed",
          );
        }
      } catch (error) {
        const errorMessage = extractErrorMessage(error);
        setError(errorMessage);
        toastr.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    document.body.className = "authentication-bg";
    return function cleanup() {
      document.body.className = "";
    };
  }, []);

  return (
    <React.Fragment>
      <div className="auth-page">
        <div className="auth-page-grid">
          {/* left brand / marketing panel */}
          <div className="auth-brand-panel">
            <div className="auth-brand-panel-inner">
              <Link to="/" className="auth-logo">
                <img
                  className="auth-logo-icon"
                  src={logo}
                  alt="Omaha Food Lovers"
                />
              </Link>
              <h1 className="auth-headline">
                Restaurant Portal
                <br />
                <span className="auth-headline-accent">Omaha Food Lovers</span>
              </h1>
              <p className="auth-subline">
                Enter your terminal code to access the restaurant dashboard.
                <br />
                Quick access for restaurant staff and terminals.
              </p>
              <ul className="auth-feature-list">
                <li>
                  <span className="auth-feature-icon" aria-hidden="true">
                    <img src={creditCardIcon} alt="Credit Card" />
                  </span>
                  <span>Track voucher redemptions and customer activity.</span>
                </li>
                <li>
                  <span className="auth-feature-icon" aria-hidden="true">
                    <img src={giftIcon} alt="Gift" />
                  </span>
                  <span>Manage your restaurant profile and settings.</span>
                </li>
                <li>
                  <span className="auth-feature-icon" aria-hidden="true">
                    <img src={zapIcon} alt="Zap" />
                  </span>
                  <span>Access platform analytics and settlement reports.</span>
                </li>
              </ul>
              <p className="auth-brand-footer">
                © Omaha Food Lovers {new Date().getFullYear()} - All Rights
                Reserved.
              </p>
            </div>
          </div>

          {/* right form panel */}
          <div className="auth-form-panel">
            <div className="auth-form-card">
              <div className="auth-form-header">
                <h2>Terminal Code Login</h2>
                <p>Enter your unique terminal code to sign in</p>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <div className="custom-input-row">
                  <CustomInput
                    label="Terminal Code"
                    name="code"
                    type="text"
                    placeholder="Enter your terminal code"
                    value={validation.values.code}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.code && !!validation.errors.code
                    }
                    errorText={validation.errors.code}
                    className="glass"
                  />
                </div>
                {error ? <p className="auth-server-error">{error}</p> : null}

                <div className="auth-submit-wrap">
                  <CustomButton
                    type="submit"
                    variant="primary"
                    iconPosition="right"
                    icon={arrowRightIcon}
                    loading={loading}
                  >
                    Sign In
                  </CustomButton>
                </div>
              </form>

              <p className="auth-login-link">
                Back to regular{" "}
                <Link to="/login?portal=restaurant">Log in</Link>
              </p>
              <p className="auth-form-footer">
                © Omaha Food Lovers {new Date().getFullYear()} - All Rights
                Reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default withRouter(RestaurantTerminalLogin);
